import { useEffect, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useNavigate } from "react-router-dom";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import { faCopy, faEnvelope, faEye, faMoneyCheckDollar, faPrint, faTrash } from "@fortawesome/free-solid-svg-icons";
import { SettlementApi, SettlementDataType } from "@/api/SettlementApi";
import { formatMoneyValue } from "@utils/Currency";
import { showAlert } from "@utils/Alert";
import styles from "./style.module.scss";
import moment from "moment";
import { QuoteDataType } from "@/api/QuoteApi";
import { OpenSendDocumentPubSub } from "@utils/pubsubs/PubSub";
import SendDocumentToEmailOffCanvas from "@components/layout/Offcanvas/SendDocumentToEmailOffCanvas";
import { useAppSelector } from "@/store/hooks";
import { SettlementFormDataKeys } from "@/generated/SettlementFormDataKeys";
import { SettlementRelatedAttributes } from "@/formTypes/SettlementDataType";
import { ProductApi } from "@/api/ProductsApi";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import LoadingScreen from "@components/content/LoadingScreen";

const SettlementIndex = () => {
  const navigate = useNavigate();
  const modelRef = useRef<ModelIndexHandle>(null);
  const suscriptionData = useAppSelector(state => state.suscription.suscription)
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
  }, []);

  const deleteRecord = async (id: string) => {
    setIsLoading(true);
    try {
      const { reduce_stock_on_settlement = false } = suscriptionData

      const settlementData = await SettlementApi.get(id, {
        selectionSet: [
          ...SettlementFormDataKeys.filter(k => !SettlementRelatedAttributes.includes(k)),
          "currency.*",
          "client.*",
          "settlementProducts.*",
        ]
      });
      
      const settlement: SettlementDataType = {
        fecha: settlementData.fecha,        
        clientId: settlementData.clientId,
        anulada: true,
        status: "anulada",
      }
      await SettlementApi.update(id, settlement);
      showAlert('success', 'La Liquidacion fue eliminado correctamente');
      if (reduce_stock_on_settlement) {
        const { settlementProducts: products } = settlementData
        ProductApi.increaseStock(products as EDocumentProductDataType[], `Ajuste de inventario por anulacion de liquidacion #${settlementData?.numero}`)

      }
    } catch (err) {
      showAlert('danger', 'La Liquidacion no puedo ser eliminado');
    }finally{
      setIsLoading(false);
    }
    setTimeout(() => {
      modelRef.current?.refetch();
    }, 300);
  }

  const triggerEmailSend = (item: QuoteDataType) => {
    OpenSendDocumentPubSub.publish({
      openOffCanvas: true,
      emails: item.email || "",
      sourceURL: `/user/settlements/print/${item.id}/0/1`,
      emailProps: {
        emailBody: suscriptionData?.settlement_email || "",
        emailFilename: `Liquidacion #${item.numero}`,
        emailSubject: `Liquidacion #${item.numero}`,
        Suscription: {
          email: suscriptionData?.email || ""
        }
      }
    })
  }

  return (
    <PageContainer
      title="Lista Liquidaciones"
      backUrl="/user/dashboard"
      primaryActions={[
        {
          onClick: () => {
            navigate('/user/settlements/add')
          },
          actionTitle: "Nuevo",
          color: "success"
        }
      ]}
    >
      <ModelIndex<SettlementDataType>
        ref={modelRef}
        meiliConfig={{
          index: "settlements",
          sort: ["numero:desc"]
        }}
        messages={{
          noResults: "No hay Liquidaciones creadas aun"
        }}
        fetchData={(...args) => SettlementApi.fetchAll(...args)}
        filterProps={[
          {
            key: "numero",
            filterType: "eq",
            type: "number"
          }
        ]}
        primaryProperties={[
          {
            key: "numero",
            highlighted: true,
            optionalHighlighted: (val: any, data: SettlementDataType) => { return data.anulada || false },
            highlightedColors: {
              "true": {
                "bg": "#dc3545",
                "text": "#fff"
              },
              "false": {
                "bg": "#1A7E9E",
                "text": "#fff"
              }
            },
            className: styles.sequence
          },
          {
            key: (d) => d.nombre_factura || d.client?.nombre || "",
            alignment: "Start"
          },
        ]}
        primaryPropertiesSizing={
          [
            { sm: "2", xs: "3" },
            { sm: "10", xs: "9" }
          ]
        }
        secondaryProperties={[
          {
            key: (data) => {
              const date = moment(data.fecha, "YYYY-MM-DD")
              return date.format("DD-MM-YYYY")
            },
            title: "Fecha",
          },
          {
            key: (data) => data.tel1 || "",
            title: "Telefono"
          },
          {
            key: (data) => {
              if ("currency_id" in data) {
                return formatMoneyValue(data.total, (data as any).currency_id)
              }
              return formatMoneyValue(data.total, data.currency.currency_id)
            },
            title: "Total",
            highlighted: true
          }
        ]}
        secondaryPropertiesSizing={
          [
            { sm: "4", md: "2" },
            { sm: "4", md: "3" },
            { sm: "4", md: "2" }
          ]
        }
        listActions={[
          {
            isVisible: (item: SettlementDataType) => item?.status == "pagada" && !!item.recibeId,
            onClick: (id, item) => window.open(`/user/recibes/print/${item.recibeId}`, '_blank'),
            actionTitle: "Ver Recibo de Pago",
            iconProps: { icon: faMoneyCheckDollar }
          },
          {
            href: "/user/settlements/copy/{:id}",
            actionTitle: "Copiar de Liquidacion",
            iconProps: { icon: faCopy }
          },
          {
            onClick: (id, item) => triggerEmailSend(item),
            actionTitle: "Enviar Por Correo ",
            iconProps: { icon: faEnvelope }
          },
          {
            href: "/user/settlements/view/{:id}",
            actionTitle: "Ver Liquidacion",
            target: "_blank",
            iconProps: { icon: faEye }
          },
          {
            href: "/user/settlements/print/{:id}/0/1",
            actionTitle: "Imprimir Liquidacion",
            target: "_blank",
            iconProps: { icon: faPrint }
          },
          {
            isVisible: (item: SettlementDataType) => !item.anulada,
            actionTitle: "Eliminar Liquidacion",
            iconProps: { icon: faTrash },
            needConfirm: true,
            confirmProps: {
              title: "Eliminar Liquidacion",
              confirmMessage: "¿Seguro que desea eliminar este Liquidacion?",
              confirmBtnText: "Eliminar"
            },
            onClick: (id: string, item: SettlementDataType) => { deleteRecord(id) }
          }
        ]}
      >
      </ModelIndex>
      <LoadingScreen isLoading={isLoading} />
      <SendDocumentToEmailOffCanvas
        title="Envio de Liquidacion a Email" />
    </PageContainer >
  );
}

export default SettlementIndex;
